/* eslint-disable prettier/prettier */
/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Grid, Backdrop } from "@mui/material";

import Loading from "../../components/loading";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ReportsBarChart from "examples/Charts/BarCharts/ReportsBarChart";
import ReportsLineChart from "examples/Charts/LineCharts/ReportsLineChart";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";

// Data
import reportsBarChartData from "layouts/dashboard/data/reportsBarChartData";
import reportsLineChartData from "layouts/dashboard/data/reportsLineChartData";

import {
  GetResumenSolicitudesDasboard,
  GetSolicitudesXentidadDasboard,
  GetSolicitudescomoEnteraronDasboard,
  GetSolicitudesSexoDasboard,
  GetSolicitudesTerapiaDasboard,
  GetSolicitudesAuxiliarDasboard,
  GetSolicitudesTamizDasboard,
  GetSolicitudesEdadDasboard,
} from "store/dashboard/dashboardActions";

// Dashboard components
import Projects from "layouts/dashboard/components/Projects";
import OrdersOverview from "layouts/dashboard/components/OrdersOverview";
import { useNavigate } from "react-router-dom";

function Dashboard() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    SolicitudesXentidad,
    SolicitudesXentidadLoading,
    SolicitudescomoEnteraron,
    SolicitudescomoEnteraronLoading,
    SolicitudesSexo,
    SolicitudesSexoLoading,
    SolicitudesTerapia,
    SolicitudesTerapiaLoading,
    SolicitudesAuxAuditivo,
    SolicitudesAuxAuditivoLoading,
    SolicitudesTamiz,
    SolicitudesTamizLoading,
    SolicitudesXedad,
    SolicitudesXedadLoading,
    ResumenSolicitudes,
    ResumenSolicitudesLoading,
  } = useSelector((state) => state.dashboard);

  const [firstLoadStarted, setFirstLoadStarted] = useState(false);
  const [firstLoadComplete, setFirstLoadComplete] = useState(false);

  useEffect(() => {
    setFirstLoadStarted(true);
    const anio = "";
    GetResumenSolicitudesDasboard(dispatch, anio);
    GetSolicitudesXentidadDasboard(dispatch, anio);
    GetSolicitudescomoEnteraronDasboard(dispatch, anio);
    GetSolicitudesSexoDasboard(dispatch, anio);
    GetSolicitudesTerapiaDasboard(dispatch, anio);
    GetSolicitudesAuxiliarDasboard(dispatch, anio);
    GetSolicitudesTamizDasboard(dispatch, anio);
    GetSolicitudesEdadDasboard(dispatch, anio);
  }, [dispatch]);

  useEffect(() => {
    if (
      firstLoadStarted &&
      !SolicitudesXentidadLoading &&
      !SolicitudescomoEnteraronLoading &&
      !SolicitudesSexoLoading &&
      !SolicitudesTerapiaLoading &&
      !SolicitudesAuxAuditivoLoading &&
      !SolicitudesTamizLoading &&
      !SolicitudesXedadLoading &&
      !ResumenSolicitudesLoading
    ) {
      setFirstLoadComplete(true);
    }
  }, [
    SolicitudesXentidadLoading,
    SolicitudescomoEnteraronLoading,
    SolicitudesSexoLoading,
    SolicitudesTerapiaLoading,
    SolicitudesAuxAuditivoLoading,
    SolicitudesTamizLoading,
    SolicitudesXedadLoading,
    ResumenSolicitudesLoading,
    firstLoadStarted,
  ]);

  const chartDataEntidades = useMemo(
    () => ({
      labels: SolicitudesXentidad.map((item) => item.entidad),
      datasets: {
        label: "Solicitudes por entidad",
        data: SolicitudesXentidad.map((item) => item.numSolicitudes),
      },
    }),
    [SolicitudesXentidad]
  );

  const chartDataEnteraron = useMemo(
    () => ({
      labels: SolicitudescomoEnteraron.map((item) => item.comoSeEntero),
      datasets: {
        label: "Número de solicitudes",
        data: SolicitudescomoEnteraron.map((item) => item.numSolicitudes),
      },
    }),
    [SolicitudescomoEnteraron]
  );

  const charDataSexo = useMemo(
    () => ({
      labels: SolicitudesSexo.map((item) => item.sexo),
      datasets: {
        label: "Número de solicitudes",
        data: SolicitudesSexo.map((item) => item.numSolicitudes),
      },
    }),
    [SolicitudesSexo]
  );

  const charDataTerapia = useMemo(
    () => ({
      labels: SolicitudesTerapia.map((item) => item.haRecibidoTerapia),
      datasets: {
        label: "Número de solicitudes",
        data: SolicitudesTerapia.map((item) => item.numSolicitudes),
      },
    }),
    [SolicitudesTerapia]
  );

  const charDataAuxAuditivo = useMemo(
    () => ({
      labels: SolicitudesAuxAuditivo.map((item) => item.utilizaAuxAuditivos),
      datasets: {
        label: "Número de solicitudes",
        data: SolicitudesAuxAuditivo.map((item) => item.numSolicitudes),
      },
    }),
    [SolicitudesAuxAuditivo]
  );

  const charDataTamiz = useMemo(
    () => ({
      labels: SolicitudesTamiz.map((item) => item.tamizNeoAuditivo),
      datasets: {
        label: "Número de solicitudes",
        data: SolicitudesTamiz.map((item) => item.numSolicitudes),
      },
    }),
    [SolicitudesTamiz]
  );

  const charDataEdad = useMemo(
    () => ({
      labels: SolicitudesXedad.map((item) => item.edad),
      datasets: {
        label: "Número de solicitudes",
        data: SolicitudesXedad.map((item) => item.numSolicitudes),
      },
    }),
    [SolicitudesXedad]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={firstLoadStarted && !firstLoadComplete}
      >
        <Loading title={"Cargando gráficas, por favor espere."} />
      </Backdrop>
      <MDBox py={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} lg={6}>
            <MDBox mb={1.5}>
              {ResumenSolicitudesLoading ? (
                <Loading title={"Cargando gráfica, por favor espere."} />
              ) : (
                <ComplexStatisticsCard
                  color="dark"
                  icon="description"
                  title="Solicitudes"
                  count={ResumenSolicitudes.numSolicitudes}
                  percentage={{
                    color: "warning",
                    amount: `${ResumenSolicitudes.numSolicitudes}`,
                    label: " Total de solicitudes",
                  }}
                  onClick={() => {
                    navigate("/solicitud");
                  }}
                />
              )}
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={6}>
            <MDBox mb={1.5}>
              {ResumenSolicitudesLoading ? (
                <Loading title={"Cargando gráfica, por favor espere."} />
              ) : (
                <ComplexStatisticsCard
                  color="info"
                  icon="topic"
                  title="Expedientes"
                  count={ResumenSolicitudes.numExpedientes}
                  percentage={{
                    color: "warning",
                    amount: `${ResumenSolicitudes.numExpedientes}`,
                    label: " Total de expedientes",
                  }}
                  onClick={() => {
                    navigate("/expedientes");
                  }}
                />
              )}
            </MDBox>
          </Grid>
        </Grid>

        <MDBox mt={4.5}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={12} lg={8}>
              <MDBox mb={3}>
                {SolicitudesXentidadLoading ? (
                  <Loading title={"Cargando gráfica, por favor espere."} />
                ) : (
                  <ReportsBarChart
                    color="primary"
                    title="Solicitudes por entidad"
                    chart={chartDataEntidades}
                    filtrarAnio={(anio) => {
                      GetSolicitudesXentidadDasboard(dispatch, anio);
                    }}
                  />
                )}
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <MDBox mb={3}>
                {SolicitudescomoEnteraronLoading ? (
                  <Loading title={"Cargando gráfica, por favor espere."} />
                ) : (
                  <ReportsBarChart
                    color="info"
                    title="¿Como se enteraron?"
                    chart={chartDataEnteraron}
                    filtrarAnio={(anio) => {
                      GetSolicitudescomoEnteraronDasboard(dispatch, anio);
                    }}
                  />
                )}
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>

        <MDBox mt={4.5}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={3} lg={3}>
              <MDBox mb={3}>
                {SolicitudesSexoLoading ? (
                  <Loading title={"Cargando gráfica, por favor espere."} />
                ) : (
                  <ReportsBarChart
                    color="info"
                    title="Sexo"
                    chart={charDataSexo}
                    filtrarAnio={(anio) => {
                      GetSolicitudesSexoDasboard(dispatch, anio);
                    }}
                  />
                )}
              </MDBox>
            </Grid>
            <Grid item xs={12} md={3} lg={3}>
              <MDBox mb={3}>
                {SolicitudesTerapiaLoading ? (
                  <Loading title={"Cargando gráfica, por favor espere."} />
                ) : (
                  <ReportsBarChart
                    color="primary"
                    title="¿Había tomado terapia?"
                    chart={charDataTerapia}
                    filtrarAnio={(anio) => {
                      GetSolicitudesTerapiaDasboard(dispatch, anio);
                    }}
                  />
                )}
              </MDBox>
            </Grid>
            <Grid item xs={12} md={3} lg={3}>
              <MDBox mb={3}>
                {SolicitudesAuxAuditivoLoading ? (
                  <Loading title={"Cargando gráfica, por favor espere."} />
                ) : (
                  <ReportsBarChart
                    color="info"
                    title="¿Usa Aux. Auditivos?"
                    chart={charDataAuxAuditivo}
                    filtrarAnio={(anio) => {
                      GetSolicitudesAuxiliarDasboard(dispatch, anio);
                    }}
                  />
                )}
              </MDBox>
            </Grid>
            <Grid item xs={12} md={3} lg={3}>
              <MDBox mb={3}>
                {SolicitudesTamizLoading ? (
                  <Loading title={"Cargando gráfica, por favor espere."} />
                ) : (
                  <ReportsBarChart
                    color="primary"
                    title="¿Tenía tamiz auditivo?"
                    chart={charDataTamiz}
                    filtrarAnio={(anio) => {
                      GetSolicitudesTamizDasboard(dispatch, anio);
                    }}
                  />
                )}
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>

        <MDBox mt={4.5}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={12} lg={8}>
              <MDBox mb={3}>
                {SolicitudesXentidadLoading ? (
                  <Loading title={"Cargando gráfica, por favor espere."} />
                ) : (
                  <ReportsBarChart
                    color="primary"
                    title="Solicitudes por edad"
                    chart={charDataEdad}
                    filtrarAnio={(anio) => {
                      GetSolicitudesEdadDasboard(dispatch, anio);
                    }}
                  />
                )}
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>

        <MDBox mt={4.5}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} lg={4}>
              <MDBox mb={3}></MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <MDBox mb={3}></MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <MDBox mb={3}></MDBox>
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Dashboard;
